import { ArrowRight, ImagePlus, Search } from 'lucide-react'
import { useDeferredValue } from 'react'
import { Link } from 'react-router'
import { usePendingInstagramScreenshots } from '../../api/instagramScreenshots'
import { pendingScreenshotLink, uniquePendingReels } from '../../lib/instagramScreenshots'
import { plural } from '../../lib/format'
import { useViewState } from '../../lib/navigation'
import { Dialog } from '../Dialog'
import { Select } from '../Select'
import { QueryState, ReelThumbnail, ReelTitle } from './Common'

export function PendingScreenshotsCard() {
  const query = usePendingInstagramScreenshots()
  if (query.isError) return (
    <div className="mb-4 rounded-xl border border-line bg-card p-4 text-sm text-muted">
      Не удалось проверить недостающие графики.{' '}
      <button className="min-h-11 text-link desk:min-h-8" onClick={() => void query.refetch()}>Повторить</button>
    </div>
  )
  if (!query.data?.count) return null
  return (
    <Link
      to="/tools?graphs=missing"
      state={{ preserveViewScroll: true }}
      className="mb-4 flex min-h-11 items-center gap-3 rounded-xl border border-line bg-card p-4 hover:bg-surface/60"
    >
      <ImagePlus size={22} className="shrink-0 text-muted" />
      <span className="min-w-0 flex-1">
        <strong className="block text-sm font-semibold">
          Подгрузите графики для {query.data.count} {plural(query.data.count, 'ролика', 'роликов', 'роликов')}
        </strong>
        <span className="mt-1 block text-sm text-muted">Удержание и отметки «Нравится» по секундам.</span>
      </span>
      <ArrowRight size={18} className="shrink-0 text-muted" />
    </Link>
  )
}

export function ScreenshotQueueDialog({ onClose }: { onClose: () => void }) {
  const query = usePendingInstagramScreenshots()
  const [search, setSearch] = useViewState('tools.screenshots.search', '')
  const [kind, setKind] = useViewState('tools.screenshots.kind', 'all')
  const [sort, setSort] = useViewState('tools.screenshots.sort', 'latest')
  const deferred = useDeferredValue(search.trim().toLocaleLowerCase('ru-RU'))
  const items = uniquePendingReels(query.data?.items ?? [])
    .filter((item) => (kind === 'all' || item.missing_kinds.includes(kind as 'retention' | 'likes')) &&
      (!deferred || `${item.caption ?? ''} ${item.shortcode}`.toLocaleLowerCase('ru-RU').includes(deferred)))
    .sort((a, b) => (sort === 'oldest' ? 1 : -1) * (a.published_at ?? '').localeCompare(b.published_at ?? ''))
  return (
    <Dialog title="Подгрузить графики" onClose={onClose} editor scrollKey="tools.screenshots.grid">
      <div data-view-ready={query.isPending ? 'false' : 'true'} className="space-y-4 pb-2">
        <p className="text-sm text-muted">Выберите ролик и добавьте скриншот недостающего графика.</p>
        <label className="relative block">
          <Search size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" />
          <span className="sr-only">Найти ролик</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Найти ролик"
            className="h-11 w-full rounded-lg bg-surface pr-3 pl-9 text-sm outline-none ring-1 ring-line focus:ring-link desk:h-8"
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <Select label="Недостающий график" value={kind} onChange={(event) => setKind(event.target.value)} className="[&_select]:h-11 desk:[&_select]:h-8">
            <option value="all">Все графики</option>
            <option value="retention">Удержание</option>
            <option value="likes">Отметки «Нравится»</option>
          </Select>
          <Select label="Сначала" value={sort} onChange={(event) => setSort(event.target.value)} className="[&_select]:h-11 desk:[&_select]:h-8">
            <option value="latest">Сначала новые</option>
            <option value="oldest">Сначала старые</option>
          </Select>
        </div>
        {!query.data ? (
          <QueryState pending={query.isPending} error={query.error} retry={query.refetch} />
        ) : !items.length ? (
          <p className="py-6 text-center text-sm text-muted">
            {query.data.count === 0 ? 'Все графики подгружены.' : 'По этим условиям роликов нет.'}
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-x-3 gap-y-5 desk:grid-cols-4">
            {items.map((item) => (
              <li key={item.shortcode}>
                <Link to={pendingScreenshotLink(item)} className="group block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-link">
                  <ReelThumbnail reel={item} className="aspect-[3/4] w-full" />
                  <span className="mt-2 block"><ReelTitle reel={item} expanded /></span>
                  <span className="mt-2 block text-xs text-muted">
                    {item.missing_kinds.length === 2 ? 'Нет обоих графиков' : item.missing_kinds[0] === 'retention' ? 'Нет удержания' : 'Нет графика лайков'}
                  </span>
                  <span className="mt-2 inline-flex min-h-11 items-center gap-1 text-sm text-link desk:min-h-8">Подгрузить <ArrowRight size={14} /></span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  )
}
